/**
 * Mailbox-nudge copy helpers
 * --------------------------
 * Presentation logic for the mailbox-nudge surfaces (the CTA matrix and
 * the pending-banner copy matrix) — extracted from the mail/mailboxNudge
 * Vuex module so the store only holds data. Consumers feed in raw
 * pieces of state and a domain string; helpers return either i18n keys
 * with params (rendered with $t in the host) or `null` when there's no
 * copy to show.
 */

export type TPendingBannerCopy = {
  key: string;
  params: Record<string, string | number>;
};

export type TNudgeCtaAction = "create" | "buy";

export type TBuyCtaInput = {
  availableLicenses: number;
  /** `mail/canBuyMoreAccounts` — the iframe-scoped partner buy gate. */
  canBuyMoreAccounts?: boolean;
};

/**
 * Whether an "add another mailbox" CTA should read Buy rather than Create.
 *
 * Buy only when there is nothing left to create against AND buying is
 * actually available for this partner/plan — the same rule that gates the
 * widget's "Buy email accounts" tile. Where it isn't, the CTA stays Create and
 * the Email accounts listing shows its used-license screen, since it already
 * gates every create on whether one is possible.
 *
 * `<= 0` rather than `=== 0`: a malformed or negative license count from the
 * backend still reads as "no capacity". `canBuyMoreAccounts` defaults to true
 * so callers outside the iframe (and existing tests) keep today's behaviour.
 */
export function shouldOfferBuy({ availableLicenses, canBuyMoreAccounts = true }: TBuyCtaInput) {
  return availableLicenses <= 0 && canBuyMoreAccounts;
}

export type TNudgeCtaDescriptor = {
  labelKey: string;
  subNoteKey: string | null;
  action: TNudgeCtaAction;
  count: number;
};

export type TPendingBannerInput = {
  /**
   * Admin self-added pending usernames — drawn from the `pendingAccounts`
   * single source of truth (admin-added rows, detected by
   * `requesterEmail` matching the current user; see the store getter
   * `adminPendingUsernames`).
   */
  adminItems: string[];
  /**
   * Team-requested pending emails (full addresses), newest/most-relevant
   * first — order-pending entries with a non-empty `requestedBy` that
   * another user requested (see the store getter `teamPendingEmails`).
   * The first entry leads the team-only banner copy.
   */
  teamItems: string[];
  /** Current domain — used to render the first admin item as a full email. */
  domain: string;
};

export type TCtaDescriptorInput = {
  accountsInList: number;
  availableLicenses: number;
  /** `mail/canBuyMoreAccounts`; defaults to true (standalone CP behaviour). */
  canBuyMoreAccounts?: boolean;
};

/**
 * Pending-accounts banner copy matrix. Returns `{ key, params }` for
 * `$t` rendering, or `null` when nothing is pending.
 */
export function getPendingBannerCopy({
  adminItems,
  teamItems,
  domain,
}: TPendingBannerInput): TPendingBannerCopy | null {
  const adminCount = adminItems.length;
  const teamCount = teamItems.length;
  if (adminCount === 0 && teamCount === 0) return null;

  // Admin-added items lead the banner (the user's own additions), with every
  // other pending account — the admin's extras plus all team requests —
  // folded into a single generic "and N more email account(s)" tail. This
  // mixed case reads identically to admin-only-with-extras (MWDC-6356), so
  // both share the adminManyOne/adminMany copy.
  if (adminCount) {
    const firstAdminEmail = `${adminItems[0]}@${domain}`;
    const others = adminCount - 1 + teamCount;
    if (others === 0) {
      return { key: "emailAccounts.pending.banner.adminOne", params: { email: firstAdminEmail } };
    }
    if (others === 1) {
      return {
        key: "emailAccounts.pending.banner.adminManyOne",
        params: { email: firstAdminEmail },
      };
    }
    return {
      key: "emailAccounts.pending.banner.adminMany",
      params: { email: firstAdminEmail, n: others },
    };
  }

  // Team-only: lead with the first team-requested email.
  const firstTeamEmail = teamItems[0];
  return teamCount === 1
    ? { key: "emailAccounts.pending.banner.teamOne", params: { email: firstTeamEmail } }
    : {
        key: "emailAccounts.pending.banner.teamMany",
        params: { email: firstTeamEmail, n: teamCount - 1 },
      };
}

/**
 * Success-screen-nudge CTA descriptor. License capacity never
 * gates adding to the list; it only flips the label/route between
 * Create and Buy.
 */
export function getNudgeCtaDescriptor({
  accountsInList,
  availableLicenses,
  canBuyMoreAccounts = true,
}: TCtaDescriptorInput): TNudgeCtaDescriptor | null {
  const n = accountsInList;
  const licenses = availableLicenses;
  // No suggestion selected → no CTA. The Buy/Create button only appears once
  // the user has queued at least one mailbox (MWDC-6282).
  if (n <= 0) return null;
  if (n === 1 && licenses >= 1) {
    return {
      labelKey: "emailAccounts.nudge.cta.createOne",
      subNoteKey: null,
      action: "create",
      count: 1,
    };
  }
  if (n >= 2 && licenses >= n) {
    return {
      labelKey: "emailAccounts.nudge.cta.createMany",
      subNoteKey: "emailAccounts.nudge.cta.createManySubNote",
      action: "create",
      count: n,
    };
  }
  // Out of capacity, but buying is suppressed for this partner/plan: keep the
  // Create copy. The listing's create gate turns the click into its
  // used-license screen (see `shouldOfferBuy`).
  // (`licenses <= 0` guard keeps the partial-license tail branch below intact.)
  if (licenses <= 0 && !shouldOfferBuy({ availableLicenses: licenses, canBuyMoreAccounts })) {
    return {
      labelKey:
        n === 1 ? "emailAccounts.nudge.cta.createOne" : "emailAccounts.nudge.cta.createMany",
      // No sub-note: "create them one at a time" doesn't apply with no
      // capacity, and the buy sub-note mentions buying.
      subNoteKey: null,
      action: "create",
      count: n,
    };
  }
  // `<= 0` rather than `=== 0`: a malformed/negative license count from the
  // backend should still read as "no capacity" and flip the CTA to Buy.
  if (n === 1 && licenses <= 0) {
    return {
      labelKey: "emailAccounts.nudge.cta.buyOne",
      subNoteKey: null,
      action: "buy",
      count: 1,
    };
  }
  if (n >= 2 && licenses <= 0) {
    return {
      labelKey: "emailAccounts.nudge.cta.buyMany",
      subNoteKey: "emailAccounts.nudge.cta.buyManySubNote",
      action: "buy",
      count: n,
    };
  }
  // 2+ requested but only `licenses` (< n) available — same UX as
  // "enough for all"; per-row Create→Buy flip happens on the next
  // success-screen render once licenses are exhausted.
  return {
    labelKey: "emailAccounts.nudge.cta.createMany",
    subNoteKey: "emailAccounts.nudge.cta.createManySubNote",
    action: "create",
    count: n,
  };
}
